import { Box } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CardDataNotFound from "../../../components/CardDataNotFound/CardDataNotFound";
import TablePaginated from "../../../components/TablePaginated/TablePaginated";
import { useIocContext } from "../../../contexts/ioc/IocContext";
import useDialogAlert from "../../../hooks/DialogAlert";
import { Types } from "../../../ioc/types";
import { IGetPipelineSchedulesQuery } from "../../../modules/operational/models/GetPipelineSchedulesQuery";
import { IOperationalService } from "../../../modules/operational/models/IOperationalService";
import { calcPagesQuantity } from "../../../utils";
import { convertToISO } from "../../../utils/dateFormatter";
import { ROW_PER_PAGE } from "../../ConsultQuotaOrders/models/SearchFilterOptions";
import { TableHeaderColumns } from "../models/TableHeaderColumns";
import {
  TableRow,
  TabsDeliveriesPipelineProps,
  clearedInitQuery,
} from "../models/default";
import { getPipelineResponseToModel } from "../models/parseResponse";
import StatusTextTag from "../../../components/StatusTextTag/StatusTextTag";
import { NO_CLEARED_ORDERS_TITLE } from "../../../utils/titles";

const ClearedPipelineDeliveries: React.FC<TabsDeliveriesPipelineProps> = ({
  onChangeCount,
  subsidiary,
  dateFilterTo,
  dateFilterFrom,
  menuIcon,
  customerCompanyIds,
}) => {
  const [total, setTotal] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [data, setData] = useState<TableRow[]>([]);
  const [errorData, setErrorData] = useState(false);
  const [query, setQuery] = useState<IGetPipelineSchedulesQuery>({
    ...clearedInitQuery,
    subsidiaryId: undefined,
    scheduleDateFrom: dateFilterFrom ? convertToISO(dateFilterFrom) : undefined,
    scheduleDateTo: dateFilterTo ? convertToISO(dateFilterTo) : undefined,
    ...(customerCompanyIds !== "" && { customerCompanyId: customerCompanyIds }),
  });
  const { snackbar } = useDialogAlert();

  const { serviceContainer } = useIocContext();
  const operationalService = serviceContainer.get<IOperationalService>(
    Types.Operational.IOperationalService
  );

  const getPipelineSchedules = useCallback(async () => {
    try {
      setIsLoadingData(true);
      setErrorData(false);
      const response = await operationalService.getPipelineSchedules(query);

      const newData = response.rows?.map(getPipelineResponseToModel) ?? [];
      const count = response.count ?? 0;

      setTotal(count);
      if (onChangeCount) onChangeCount(count);
      setData(newData);

      if (newData.length === 0) {
        setErrorData(true);
      }
    } catch (error) {
      setErrorData(true);
      snackbar({
        message: "Erro ao carregar entregas dutoviárias",
        variant: "error",
      });
    } finally {
      setIsLoadingData(false);
    }
  }, [query]);

  useEffect(() => {
    getPipelineSchedules();
  }, [query]);

  useEffect(() => {
    setQuery((oldState) => ({
      ...oldState,
      subsidiaryId: subsidiary,
      ...(dateFilterFrom &&
        dateFilterTo && { scheduleDateFrom: convertToISO(dateFilterFrom) }),
      ...(dateFilterFrom &&
        dateFilterTo && { scheduleDateTo: convertToISO(dateFilterTo) }),
      ...(customerCompanyIds !== "" && {
        customerCompanyId: customerCompanyIds,
      }),
    }));
  }, [dateFilterFrom, dateFilterTo, customerCompanyIds, setQuery, subsidiary]);

  return (
    <>
      {errorData ? (
        <Box alignSelf={"center"}>
          <CardDataNotFound
            icon="data-not-found"
            title={NO_CLEARED_ORDERS_TITLE}
          />
        </Box>
      ) : (
        <TablePaginated
          loadFromServer
          isLoadingData={isLoadingData}
          columns={TableHeaderColumns}
          data={data.map((item, index) => ({
            ...item,
            statusIcon: <StatusTextTag value={item.statusText} />,
            menuIcon: menuIcon?.(item),
          }))}
          paginationProps={{
            pagesTotal: calcPagesQuantity(ROW_PER_PAGE, total),
            page: query.page,
          }}
          resultsTotal={total}
          rowsPerPage={ROW_PER_PAGE}
          onChangePage={(page) => {
            setQuery((oldState) => ({
              ...oldState,
              page,
            }));
          }}
        />
      )}
    </>
  );
};

export default ClearedPipelineDeliveries;
